import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Table, Tooltip, DatePicker, Input, Select, Form } from "antd";
import moment, { Moment } from "moment";
import pencilWithCalendarIcon from "../../../img/pencil-calendar.svg";
import {
  displayFormat,
  toDisplayFormat,
  toHttpFormat,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
} from "../../../lib/utils/formatDate";
import tickIcon from "../../../img/done.svg";
import deleteIcon from "../../../img/delete-round.svg";
import pencilWithPlusIcon from "../../../img/pencil-plus.svg";
import edit from "../../../img/edit.svg";
import dataTypeIdesCodes from "../../../lib/const/dataTypeIdesCodes";
import { fetchDicType } from "../../../store/slices/rtkDictionaries";
import { checkIsLoaded, checkIsLoading } from "../../../lib/utils/checkLoadingStatus";
import propertiesMultiPlyStatusCodes from "../../../lib/const/propertiesMultiPlyStatusCodes";
import { useAppSelector } from "../../..";
import { DictionaryDto, Property, TableColumn, PropertyTableData } from "../../../lib/interfaces";
import "./PropertyVersionsModal.less";

interface ComponentProps {
  dateObjectToForCalendar: string | undefined;
  dicTypeWithParameter: DictionaryDto[];
  getPropValue: (props: Partial<Property>) => string | number;
  handleSetPropertiesRequestBody: (props: Partial<Property>) => void;
  handleSubmit: () => void;
  hasAccessToEdit: boolean;
  isEditing: (rec: PropertyTableData) => boolean;
  isEditingForceProperty: boolean;
  isEditingPropertyToDate: boolean;
  isMakingNewProperty: boolean;
  isMakingNewPropertyVersion: boolean;
  propertiesRequestBody: Partial<Property>;
  resetPropertiesRequestBody: () => void;
  setCloseDateInPast: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingKey: React.Dispatch<React.SetStateAction<string | undefined>>;
  setFormPropertyEditStatus: React.Dispatch<React.SetStateAction<string>>;
}

const PropertyVersionsModal = ({
  dateObjectToForCalendar,
  dicTypeWithParameter,
  getPropValue,
  handleSetPropertiesRequestBody,
  handleSubmit,
  hasAccessToEdit,
  isEditing,
  isEditingForceProperty,
  isEditingPropertyToDate,
  isMakingNewProperty,
  isMakingNewPropertyVersion,
  propertiesRequestBody,
  resetPropertiesRequestBody,
  setCloseDateInPast,
  setEditingKey,
  setFormPropertyEditStatus,
}: ComponentProps) => {
  const { dictionaries } = useAppSelector((state) => state.rtkDictionaries);
  const { propertiesVersions, propertiesVersionsLoading } = useAppSelector(
    (state) => state.properties
  );

  const { propertyTypesByObjectType } = useAppSelector((state) => state?.propertyTypesAdmin);

  const [dataVersions, setDataVersions] = useState<PropertyTableData[]>([]);
  console.log({ dataVersions });
  const [parentVersionSpecificDicId, setParentVersionSpecificDicId] = useState<number>();

  const [customParameters, setCustomParameters] = useState<DictionaryDto[]>([]);

  const dispatch = useDispatch();
  const dateFormat = displayFormat;

  // данные для отображения
  useEffect(() => {
    if (checkIsLoaded(propertiesVersionsLoading)) {
      setDataVersions(
        propertiesVersions.map(
          // TODO разобраться, почему типизация setTableData не срабатывает. Пришлось сюда прописать
          (item, index): PropertyTableData => {
            const nowDate = toHttpFormat(moment());
            const propertyType =
              item.propertyType ||
              propertyTypesByObjectType.find(({ id }) => id === item.propertyTypeId);
            return {
              ...item,
              key: `${item.id}_${item.fromDate}`,
              propertyType,
              currentDate: moment().isBetween(item.fromDate, item.toDate),
              pastDate: moment(item.toDate).isBefore(nowDate),
              futureDate: moment(item.fromDate).isAfter(nowDate),
              lastVersion: index === 0 ? "last-version" : "",
            };
          }
        )
      );
    }
  }, [propertiesVersions, propertiesVersionsLoading, propertyTypesByObjectType]);

  // Существующие параметры для каждого существующего свойства с multiTypeId === 4 (propertiesMultiPlyStatusCodes.many_single_by_dictionary)
  const getExistedManySingleParameters = () => {
    const manySinglePropsTypeIds: { [key: number]: number[] } = {};
    propertiesVersions.forEach(({ propertyType, specificDicId }) => {
      if (
        propertyType?.multiplTypeId === propertiesMultiPlyStatusCodes.many_single_by_dictionary &&
        specificDicId
      ) {
        // Оставление в справочнике существующего параметра при редактировании
        if (specificDicId !== parentVersionSpecificDicId) {
          if (propertyType.id in manySinglePropsTypeIds) {
            manySinglePropsTypeIds[propertyType.id].push(specificDicId);
          } else {
            manySinglePropsTypeIds[propertyType.id] = [specificDicId];
          }
        }
      }
    });
    return manySinglePropsTypeIds;
  };

  // Наполнение словаря редактируемых параметров
  useEffect(() => {
    if (
      dictionaries &&
      propertiesRequestBody.propertyType &&
      propertiesRequestBody.propertyType?.specificDicTypeId
    ) {
      const findDicName = Object.entries(dictionaries).find(
        ([, dicValues]) =>
          dicValues?.[0]?.dicTypeId === propertiesRequestBody.propertyType?.specificDicTypeId
      )?.[0];

      const parametersDictionary =
        dictionaries?.[findDicName || propertiesRequestBody.propertyType.specificDicTypeId];
      if (parametersDictionary && isMakingNewPropertyVersion) {
        // Версии не пересекаются, поэтому при добавлении версии доступны все значения
        setCustomParameters(parametersDictionary);
      } else if (
        parametersDictionary &&
        isEditingForceProperty &&
        propertiesRequestBody.propertyTypeId &&
        propertiesRequestBody.propertyType?.id
      ) {
        const existedManySingleParameters = getExistedManySingleParameters();
        const filteredParameters = parametersDictionary.filter(
          (parameter) =>
            // TODO разобраться почему несмотря на наличие проверки propertiesRequestBody.propertyType, выдается "is possible undefined"
            !existedManySingleParameters[propertiesRequestBody.propertyType!.id]?.includes(
              parameter.id
            )
        );
        setCustomParameters(filteredParameters);
      } else {
        setCustomParameters([]);
      }
    } else {
      setCustomParameters([]);
    }
  }, [propertiesVersions, dictionaries, propertiesRequestBody]);

  const columnsModalPropertyVersions: TableColumn<PropertyTableData>[] = [
    {
      title: "Наименование",
      dataIndex: "propertyTypeId",
      key: "propertyTypeId",
      width: "22%",
      render: (x, record) => record.propertyType?.name,
    },
    {
      title: "Параметры",
      dataIndex: "specificDicId",
      key: "specificDicId",
      width: "20%",
      render: (val, record) => {
        const editable = isEditing(record);

        if (editable && (isMakingNewPropertyVersion || isEditingForceProperty)) {
          const onChangeDicType = (specificDicId) => {
            handleSetPropertiesRequestBody({
              specificDicId,
              stringValue: undefined,
              numberValue: undefined,
              dateValue: undefined,
              dictionaryValue: undefined,
            });
          };

          return (
            <Form.Item
              className="form-item-required__hidden-error__mb0"
              name="specificDicId"
              rules={[
                {
                  required: !!record.propertyType?.specificDicTypeId,
                },
              ]}
            >
              <Select
                placeholder="Выберите значение из словаря specific"
                showSearch
                style={{ width: "236px" }}
                disabled={!record.propertyType?.specificDicTypeId}
                onChange={onChangeDicType}
                filterOption={(input, option) =>
                  option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {customParameters.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          );
        }
        const getParameterName = () => {
          if (dictionaries && record.propertyType?.specificDicTypeId) {
            const findDicName = Object.entries(dictionaries).find(
              ([, dicValues]) =>
                dicValues?.[0]?.dicTypeId === record.propertyType?.specificDicTypeId
            )?.[0];

            return (
              dictionaries?.[findDicName || record.propertyType.specificDicTypeId].find(
                ({ id }) => id === record.specificDicId
              )?.name || ""
            );
          }
          return "";
        };

        return <>{getParameterName()}</>;
      },
    },
    {
      title: "Значение",
      dataIndex: "values",
      key: "values",
      width: "25%",
      render: (val, record) => {
        const editable = isEditing(record);

        const onChangeValueNum = (e) => {
          const { value } = e.target;
          const reg = /^-?\d*(\.\d*)?$/;
          if ((!Number.isNaN(value) && reg.test(value)) || value === "" || value === "-") {
            handleSetPropertiesRequestBody({
              numberValue: Number(value),
            });
          }
        };

        const onChangeValueString = (e) => {
          handleSetPropertiesRequestBody({
            stringValue: e.target.value,
          });
        };

        const onChangeDate = (date: Moment | null) => {
          handleSetPropertiesRequestBody({
            dateValue: toHttpFormatStartOfDay(date),
          });
        };

        const onChangeDicType = (dictionaryValue) => {
          handleSetPropertiesRequestBody({
            dictionaryValue,
          });
        };

        const chooseValue = () => {
          const dataType = record.propertyType?.dataType;

          if (dataType === dataTypeIdesCodes.number) {
            return (
              <Form.Item
                name="numberValue"
                className="form-item-required__hidden-error__mb0"
                rules={[
                  {
                    required: true,
                  },
                  {
                    pattern: /^(|[0-9]*[.]?[0-9])+$/,
                  },
                ]}
              >
                <Input onChange={onChangeValueNum} placeholder="Введите числовое значение" />
              </Form.Item>
            );
          }
          if (dataType === dataTypeIdesCodes.string) {
            return (
              <Form.Item
                name="stringValue"
                className="form-item-required__hidden-error__mb0"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input onChange={onChangeValueString} placeholder="Введите строковое значение" />
              </Form.Item>
            );
          }
          if (dataType === dataTypeIdesCodes.date) {
            return (
              <Form.Item
                name="dateValue"
                className="form-item-required__hidden-error__mb0"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <DatePicker format={dateFormat} onChange={onChangeDate} allowClear={false} />
              </Form.Item>
            );
          }
          if (dataType === dataTypeIdesCodes.dictionary) {
            return (
              <Form.Item
                name="dictionaryValue"
                className="form-item-required__hidden-error__mb0"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Выберите значение из словаря"
                  style={{ width: "285px" }}
                  onChange={onChangeDicType}
                  filterOption={(input, option) =>
                    option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {dicTypeWithParameter.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            );
          }
          return null;
        };

        return editable && (isMakingNewPropertyVersion || isEditingForceProperty) ? (
          <>{chooseValue()}</>
        ) : (
          <>{getPropValue(record)}</>
        );
      },
    },
    {
      title: "Дата с",
      dataIndex: "fromDate",
      key: "fromDate",
      width: "10%",
      render: (fromDate, record) => {
        const editable = isEditing(record);

        const onChangeDate = (date) => {
          handleSetPropertiesRequestBody({
            fromDate: toHttpFormatStartOfDay(date),
          });
        };

        const disabledDates = (cur) => cur.isBefore(fromDate) || cur.isAfter(record.toDate);

        return editable && isMakingNewPropertyVersion ? (
          <Form.Item
            name="value5"
            className="form-item-required__hidden-error__mb0"
            initialValue={moment(fromDate)}
            rules={[
              {
                required: true,
              },
            ]}
          >
            <DatePicker
              format={dateFormat}
              onChange={onChangeDate}
              style={{ width: "110px" }}
              disabledDate={disabledDates}
              allowClear={false}
            />
          </Form.Item>
        ) : (
          <>{fromDate ? toDisplayFormat(fromDate) : ""}</>
        );
      },
    },
    {
      title: "Дата по",
      dataIndex: "toDate",
      key: "toDate",
      width: "10%",
      render: (toDate, record) => {
        const editable = isEditing(record);
        const nowDate = moment();

        const disabledDate = (current) =>
          current.isBefore(record.fromDate) ||
          (dateObjectToForCalendar && current.isAfter(dateObjectToForCalendar));

        const onChangeDate = (date) => {
          handleSetPropertiesRequestBody({
            toDate: toHttpFormatEndOfDay(date),
          });
          if (moment(date).isBefore(nowDate)) {
            setCloseDateInPast(true);
          } else setCloseDateInPast(false);
        };

        return editable && isEditingPropertyToDate ? (
          <>
            <DatePicker
              format={dateFormat}
              onChange={onChangeDate}
              style={{ width: "110px" }}
              defaultValue={moment(toDate)}
              disabledDate={disabledDate}
              allowClear={false}
            />
          </>
        ) : (
          <>{toDate ? toDisplayFormat(toDate) : ""}</>
        );
      },
    },
    {
      dataIndex: "btns",
      key: "btns",

      render: (val, record, index) => {
        const editable = isEditing(record);
        const disabled =
          isMakingNewPropertyVersion || isEditingForceProperty || isMakingNewProperty;

        const handleEdit = (propertyEditStatus) => () => {
          if (record.propertyType?.dataType === dataTypeIdesCodes.dictionary) {
            if (record.propertyType?.valDicTypeAlias) {
              dispatch(fetchDicType(record.propertyType.valDicTypeAlias));
            }
          }

          setParentVersionSpecificDicId(record.specificDicId);

          handleSetPropertiesRequestBody(record);

          setEditingKey(() => record.key);
          setFormPropertyEditStatus(propertyEditStatus);
        };

        const addNewVersion = (propertyEditStatus) => () => {
          const nextFromDate = toHttpFormatStartOfDay(moment(record.fromDate).add(1, "day"));

          if (record.propertyType?.dataType === dataTypeIdesCodes.dictionary) {
            if (record.propertyType?.valDicTypeAlias) {
              dispatch(fetchDicType(record.propertyType.valDicTypeAlias));
            }
          }

          handleSetPropertiesRequestBody({
            ...record,
            fromDate: nextFromDate,
          });

          setEditingKey(() => `new${record.key}`);
          setFormPropertyEditStatus(propertyEditStatus);

          const newRecord = {
            ...record,
            key: `new${record.key}`,
            fromDate: nextFromDate,
          };

          setDataVersions((prev) => [...prev.slice(0, index), newRecord, ...prev.slice(index)]);
        };

        const handleCancel = () => {
          setEditingKey(undefined);
          if (isMakingNewPropertyVersion)
            setDataVersions((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
          setFormPropertyEditStatus("");
          resetPropertiesRequestBody();
        };
        if (hasAccessToEdit) {
          const addVersionAvailable =
            record.fromDate && moment(record.fromDate).isBefore(record.toDate, "day");
          return editable ? (
            <div className="property-user-bar">
              <Tooltip title="Подтвердить изменения">
                <Button
                  icon={<img src={tickIcon} alt="tick" width="16px" />}
                  className="icon"
                  type="text"
                  htmlType="button"
                  onClick={() => {
                    handleSubmit();
                    setParentVersionSpecificDicId(undefined);
                  }}
                />
              </Tooltip>
              <Button
                icon={<img src={deleteIcon} width="16px" alt="delete" />}
                type="text"
                className="icon"
                htmlType="button"
                onClick={() => handleCancel()}
              />
            </div>
          ) : (
            <div className="property-user-bar">
              <Tooltip title="Добавить версию">
                {addVersionAvailable && (record.currentDate || record.futureDate) && (
                  <Button
                    icon={<img src={pencilWithPlusIcon} alt="pencil with plus" width="16px" />}
                    type="text"
                    htmlType="button"
                    onClick={addNewVersion("createNewPropertyVersion")}
                    disabled={disabled}
                  />
                )}
              </Tooltip>

              <Tooltip title="Редактировать">
                {!record.pastDate && (
                  <Button
                    icon={<img src={edit} alt="pencil" width="16px" />}
                    type="text"
                    htmlType="button"
                    disabled={disabled}
                    onClick={handleEdit("editingForceProperty")}
                  />
                )}
              </Tooltip>

              <Tooltip title="Изменить дату закрытия">
                {record.lastVersion === "last-version" && !record.pastDate && (
                  <Button
                    icon={
                      <img src={pencilWithCalendarIcon} alt="pencil with calendar" width="18px" />
                    }
                    type="text"
                    htmlType="button"
                    disabled={disabled}
                    onClick={handleEdit("editingPropertyToDate")}
                  />
                )}
              </Tooltip>
            </div>
          );
        }
      },
    },
  ];

  const buildRowClassName = (record) => {
    const editable = isEditing(record);
    const editNew = record.key ? record.key.includes("new") : false;
    const activeClassName = "property-version-row";

    if (record.currentDate && !editable) return `${activeClassName} current-date`;
    if (record.currentDate && editable && !editNew)
      return `${activeClassName} current-date active-row`;
    if (record.currentDate && editable && editNew)
      return `${activeClassName} current-date active-row new-prop`;
    if (record.pastDate && !editable) return `${activeClassName} past-date`;
    if (record.pastDate && editable) return `${activeClassName} past-date active-row`;
    if (record.futureDate && !editable) return `${activeClassName} future-date`;
    if (record.futureDate && editable) return `${activeClassName} future-date active-row`;
    return "";
  };

  const innerTable = () => (
    <div className="version-inner-table property-inner-table">
      <Table
        loading={checkIsLoading(propertiesVersionsLoading)}
        className="inner-table"
        size="small"
        columns={columnsModalPropertyVersions}
        dataSource={dataVersions}
        pagination={false}
        showHeader={false}
        rowClassName={buildRowClassName}
      />
    </div>
  );

  return <>{innerTable()}</>;
};

export default PropertyVersionsModal;
